import { tokens } from '@neo4j-ndl/base';
import { Button, IconButton, Typography } from '@neo4j-ndl/react';
import { ArrowPathIconOutline, ChevronRightIconOutline } from '@neo4j-ndl/react/icons';
import { type FleetManagementTypes, useOpsContext } from '@nx/state';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { ApiErrorBanner } from '../shared/components';
import { DashboardLineCard } from './charts/line-cards';
import type { ChartContext } from './charts/types';
import { chartKey, useMetricsQuery, useMetricsViewState } from './hooks';
import { useViewCharts } from './hooks/use-view-charts';

interface DeploymentDashboardMetricsProps {
  dbmsDetails: FleetManagementTypes.DbmsDetails;
  projectId: string;
  chartContext: ChartContext;
}

const DeploymentDashboardMetrics = ({ dbmsDetails, projectId, chartContext }: DeploymentDashboardMetricsProps) => {
  const deploymentDashboardCharts = useViewCharts(undefined, false).DEPLOYMENT_DASHBOARD;
  const selectedEntityDetails = useMemo(
    () => ({
      tenantId: projectId,
      dbmsId: dbmsDetails.dbmsId,
      name: dbmsDetails.name,
    }),
    [projectId, dbmsDetails.dbmsId, dbmsDetails.name],
  );

  const metricsResult = useMetricsQuery({
    selectedEntityDetails,
    timeRange: chartContext.timeRange,
    chartProps: deploymentDashboardCharts,
    chartType: 'DEPLOYMENT_DASHBOARD',
    useAggregations: false,
    isFleetDbms: true,
  });

  if (metricsResult.error) {
    return <ApiErrorBanner error={metricsResult.error} />;
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {deploymentDashboardCharts.map((chart) => (
        <DashboardLineCard
          key={chartKey(chart)}
          multiChartProps={chart}
          context={chartContext}
          metricsResult={metricsResult}
        />
      ))}
    </div>
  );
};

export const DeploymentResources = ({
  dbmsDetails,
  projectId,
}: {
  dbmsDetails: FleetManagementTypes.DbmsDetails;
  projectId: string;
}) => {
  const [showResources, setShowResources] = useState(false);
  const navigate = useNavigate();
  const { setSelectedInstance } = useOpsContext();

  const { chartContext, refreshTimeRange } = useMetricsViewState({ timePeriod: '24H' });

  const handleToggleResource = () => {
    setShowResources((show) => !show);
  };

  const handleViewAllMetrics = () => {
    setSelectedInstance(dbmsDetails.dbmsId);
    void navigate(`/projects/${projectId}/metrics`);
  };

  return (
    <div aria-label={`Deployment metrics for ${dbmsDetails.name}`} className="relative flex flex-col gap-4 p-4">
      <div className="flex w-full justify-between">
        <div
          role="button"
          tabIndex={0}
          aria-label="Toggle Metrics"
          aria-pressed={showResources}
          onKeyDown={(e) => e.key === 'Enter' && handleToggleResource()}
          className="text-palette-neutral-text-weak flex cursor-pointer items-center gap-2"
          onClick={handleToggleResource}
        >
          <ChevronRightIconOutline
            aria-label="Show Metrics"
            className="size-4"
            style={{
              transform: showResources ? 'rotate(90deg)' : 'rotate(0deg)',
              transitionDuration: tokens.transitions.values.duration.quick,
            }}
          />
          {/* add margin right to balance tab outline */}
          <Typography variant="subheading-small" className="mr-2">
            Metrics
          </Typography>
        </div>

        <div className="flex items-center gap-2">
          {showResources && (
            <IconButton
              ariaLabel="Click to refresh charts"
              size="small"
              onClick={refreshTimeRange}
              isClean={true}
              htmlAttributes={{
                title: 'Click to refresh charts',
              }}
            >
              <ArrowPathIconOutline />
            </IconButton>
          )}
          <Button fill="outlined" size="small" color="neutral" onClick={handleViewAllMetrics}>
            View all metrics
          </Button>
        </div>
      </div>
      {showResources && (
        <DeploymentDashboardMetrics dbmsDetails={dbmsDetails} projectId={projectId} chartContext={chartContext} />
      )}
    </div>
  );
};
